import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { User, Loader2 } from 'lucide-react'
import { profileApi } from '@/services/profile-api'
import { useAuthStore } from '@/stores/auth-store'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { extractErrorMessage } from '@/lib/errors'

export default function ProfileStep() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const [fullName, setFullName] = useState(user?.full_name || '')
  const [jobTitle, setJobTitle] = useState('')
  const [phone, setPhone] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingProfile, setIsLoadingProfile] = useState(true)

  // Pre-load existing profile data so returning users see their info
  useEffect(() => {
    let cancelled = false
    const loadProfile = async () => {
      try {
        const profile = await profileApi.getMe()
        if (cancelled) return
        setFullName(profile.full_name || '')
        setJobTitle(profile.job_title || '')
        setPhone(profile.phone || '')
      } catch {
        // Profile may not exist yet for new users — that's ok
      } finally {
        if (!cancelled) setIsLoadingProfile(false)
      }
    }
    loadProfile()
    return () => {
      cancelled = true
    }
  }, [])

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsSubmitting(true)
    try {
      await profileApi.updateMe({ full_name: fullName, job_title: jobTitle, phone })
      navigate('/onboarding/organization')
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Could not save profile'))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoadingProfile) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Step 1: Your Profile</h2>
          <p className="text-muted-foreground mt-1">Tell us a bit about yourself</p>
        </div>
        <div className="flex items-center justify-center py-6">
          <Skeleton className="h-16 w-16 rounded-full" />
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-10 w-full" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground">Step 1: Your Profile</h2>
        <p className="text-muted-foreground mt-1">Tell us a bit about yourself</p>
      </div>

      <div className="flex items-center justify-center py-6">
        <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
          <User className="h-8 w-8 text-primary" />
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="fullName">Full Name *</Label>
          <Input
            id="fullName"
            placeholder="John Doe"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            disabled={isSubmitting}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="jobTitle">Job Title</Label>
          <Input
            id="jobTitle"
            placeholder="Product Manager"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
            disabled={isSubmitting}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">Phone</Label>
          <Input
            id="phone"
            type="tel"
            placeholder="+1 (555) 123-4567"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={isSubmitting}
          />
        </div>
      </div>

      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Saving...
          </>
        ) : (
          'Continue'
        )}
      </Button>
    </form>
  )
}
